import { Box, Button, InputAdornment, TextField } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { motion } from "framer-motion";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import BusinessIcon from "@mui/icons-material/Business";
import CommentIcon from "@mui/icons-material/Comment";
import React, { useState, useCallback, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { debounce } from "lodash";

// Define animation styles outside the component to prevent recreation
const buttonHoverAnimation = { scale: 1.05 };
const buttonTapAnimation = { scale: 0.98 };

export default function MeetingInfoForm({ formData, handleChange, activeStep, handleNext }) {
    const { t } = useTranslation();
    const theme = useTheme();
    const [errors, setErrors] = useState({
        name: "",
        email: "",
        company: "",
        additionalInfo: ""
    });
    const [isHovered, setIsHovered] = useState(false);
    const [isButtonPressed, setIsButtonPressed] = useState(false);

    // Create a memoized validation function that won't be recreated on every render
    const validateField = useCallback((name, value) => {
        switch (name) {
            case "name":
                return value.trim().length === 0 ? t("drawer.meeting.nameRequired", "Name is required") : "";
            case "email":
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                return !value ? t("drawer.meeting.emailRequired", "Email is required") : !emailRegex.test(value) ? t("drawer.meeting.invalidEmail", "Invalid email format") : "";
            case "company":
                return value.trim().length === 0 ? t("drawer.meeting.companyRequired", "Company Name is required") : "";
            case "additionalInfo":
                return "";
            default:
                return "";
        }
    }, [t]);

    // Create a debounced validation function with useRef to ensure it's not recreated
    const debouncedValidateRef = useRef(
        debounce((name, value, callback) => {
            const error = validateField(name, value);
            callback(name, error);
        }, 600)
    ).current;

    // Function to update a single error without rerendering the entire error state
    const updateSingleError = useCallback((name, errorMessage) => {
        setErrors(prev => ({
            ...prev,
            [name]: errorMessage
        }));
    }, []);

    // Flag to track if initial validations have been run
    const validationRunRef = useRef(false);

    // Optimized change handler that calls debounced validation
    const handleFieldChange = useCallback((e) => {
        const { name, value } = e.target;
        handleChange(e);
        if (e.type === 'blur' || validationRunRef.current) {
            debouncedValidateRef(name, value, updateSingleError);
        }
    }, [handleChange, updateSingleError, debouncedValidateRef]);

    // Validate all fields on submit
    const handleSubmit = useCallback(() => {
        validationRunRef.current = true;
        const newErrors = {
            name: validateField("name", formData.name),
            email: validateField("email", formData.email),
            company: validateField("company", formData.company),
            additionalInfo: validateField("additionalInfo", formData.additionalInfo || "")
        };
        setErrors(newErrors);
        if (!Object.values(newErrors).some((error) => error)) {
            handleNext();
        }
    }, [formData, handleNext, validateField]);

    // Create a memoized check for form validity
    const isFormValid = useMemo(() => {
        return (
            formData.name?.trim() &&
            formData.email?.trim() &&
            formData.company?.trim() &&
            !errors.name &&
            !errors.email &&
            !errors.company
        );
    }, [formData.name, formData.email, formData.company, errors.name, errors.email, errors.company]);

    // Memoize the button styles
    const buttonStyles = useMemo(() => ({
        mt: 4,
        alignSelf: "flex-start",
        px: 4,
        py: 2,
        fontSize: "1rem",
        fontWeight: 600,
        borderRadius: 12,
        textTransform: "none",
        background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
        color: "#FFFFFF",
        boxShadow: isHovered
            ? `0 8px 24px ${theme.palette.primary.main}40`
            : `0 4px 12px ${theme.palette.primary.main}30`,
        transition: "all 0.3s ease",
        "&:hover": {
            boxShadow: `0 12px 32px ${theme.palette.primary.main}50`,
            transform: "translateY(-2px)",
            background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
        },
    }), [isHovered, theme]);

    // Precreate field configurations
    const fieldConfigs = useMemo(() => [
        {
            id: "name",
            name: "name",
            label: t("drawer.meeting.name"),
            value: formData.name,
            required: true,
            error: !!errors.name,
            helperText: errors.name,
            icon: <PersonIcon sx={{ color: theme.palette.primary.main }} />
        },
        {
            id: "email",
            name: "email",
            label: t("drawer.meeting.email"),
            value: formData.email,
            type: "email",
            required: true,
            error: !!errors.email,
            helperText: errors.email,
            icon: <EmailIcon sx={{ color: theme.palette.primary.main }} />
        },
        {
            id: "company",
            name: "company",
            label: t("drawer.meeting.company"),
            value: formData.company,
            required: true,
            error: !!errors.company,
            helperText: errors.company,
            icon: <BusinessIcon sx={{ color: theme.palette.primary.main }} />
        },
        {
            id: "additionalInfo",
            name: "additionalInfo",
            label: t("drawer.meeting.additionalInfo"),
            value: formData.additionalInfo || "",
            multiline: true,
            rows: 4,
            error: !!errors.additionalInfo,
            helperText: errors.additionalInfo,
            icon: <CommentIcon sx={{ color: theme.palette.primary.main }} />
        }
    ], [formData, errors, t, theme]);

    // Render each text field from the configuration
    const renderTextField = (config) => (
        <Box key={config.id}>
            <TextField
                fullWidth
                id={config.id}
                name={config.name}
                label={config.label}
                value={config.value}
                onChange={handleFieldChange}
                onBlur={handleFieldChange}
                required={config.required}
                variant="outlined"
                type={config.type || "text"}
                multiline={config.multiline}
                rows={config.rows}
                error={config.error}
                helperText={config.helperText}
                InputProps={{
                    startAdornment: config.icon ? (
                        <InputAdornment position="start">
                            {config.icon}
                        </InputAdornment>
                    ) : null,
                }}
                sx={{
                    "& .MuiOutlinedInput-root": {
                        backgroundColor: theme.palette.background.paper,
                        color: theme.palette.text.primary,
                        "& fieldset": {
                            borderColor: theme.palette.grey[300],
                            borderWidth: 1,
                        },
                        "&:hover fieldset": {
                            borderColor: theme.palette.primary.main,
                            borderWidth: 2,
                        },
                        "&.Mui-focused fieldset": {
                            borderColor: theme.palette.primary.main,
                            borderWidth: 2,
                        },
                        "& input": {
                            color: theme.palette.text.primary,
                            "&::placeholder": {
                                color: theme.palette.text.secondary,
                                opacity: 1,
                            },
                        },
                        "& textarea": {
                            color: theme.palette.text.primary,
                            "&::placeholder": {
                                color: theme.palette.text.secondary,
                                opacity: 1,
                            },
                        },
                    },
                    "& .MuiInputLabel-root": {
                        color: theme.palette.text.secondary,
                        "&.Mui-focused": {
                            color: theme.palette.primary.main,
                        },
                        "&.MuiInputLabel-shrink": {
                            color: theme.palette.primary.main,
                        },
                    },
                    "& .MuiFormHelperText-root": {
                        color: theme.palette.error.main,
                        "&.Mui-error": {
                            color: theme.palette.error.main,
                        },
                    },
                }}
            />
        </Box>
    );

    if (activeStep !== 0) {
        return null;
    }

    return (
        <Box
            component="form"
            sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2, // Reduced gap for mobile
                flex: 1,
                maxHeight: { xs: "80vh", sm: "auto" }, // Limit height on mobile
                overflowY: { xs: "auto", sm: "visible" }, // Enable scroll on mobile
                p: { xs: 2, sm: 3 }, // Adjust padding for mobile
            }}
        >
            {fieldConfigs.map(renderTextField)}
            <Box sx={{ mt: "auto", pt: 2 }}>
                <motion.div
                    whileHover={buttonHoverAnimation}
                    whileTap={buttonTapAnimation}
                    style={{ willChange: 'transform', transformStyle: 'preserve-3d' }}
                >
                    <Button
                        fullWidth
                        variant="contained"
                        onClick={handleSubmit}
                        onMouseEnter={() => setIsHovered(true)}
                        onMouseLeave={() => setIsHovered(false)}
                        onMouseDown={() => setIsButtonPressed(true)}
                        onMouseUp={() => setIsButtonPressed(false)}
                        disabled={!isFormValid}
                        sx={{
                            ...buttonStyles,
                            width: { xs: "100%", sm: "auto" }, // Full width on mobile
                            fontSize: { xs: "1rem", sm: "1.1rem" }, // Slightly smaller font on mobile
                        }}
                    >
                        {t("drawer.meeting.continueButton")}
                    </Button>
                </motion.div>
            </Box>
        </Box>
    );
}